import { useState } from 'react';
import type { RegistoSiadap } from '@/context/SiadapContext';
import type { AvaliacaoSiadap } from '@/context/SiadapContext';
import type { Funcionario } from '@/context/FuncionariosContext';
import { pontosParaAvaliacao } from '@/utils/progressaoUtils';

interface SiadapRowProps {
  registo: RegistoSiadap;
  funcionarios: Funcionario[];
  onEdit: () => void;
  onDelete: () => void;
  onNameClick: (funcionario: Funcionario) => void;
}

type AvaliacaoKey = AvaliacaoSiadap | 'Relevante' | 'Adequado';

const avaliacaoConfig: Record<AvaliacaoKey, { bg: string; text: string; dot: string }> = {
  Excelente: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  Bom: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  Regular: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400' },
  Inadequado: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  Relevante: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
  Adequado: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-400' },
};

// Determina se o período é anterior a 2025
function isEscalaAntiga(periodo: string): boolean {
  const partes = (periodo ?? '').trim().split('-');
  const ultimo = partes[partes.length - 1];
  const ano = parseInt(ultimo, 10);
  return !isNaN(ano) && ano < 2025;
}

const fallbackConfig = { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' };

const avatarColors = [
  'bg-rose-100 text-rose-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
];

export default function SiadapRow({ registo, funcionarios, onEdit, onDelete, onNameClick }: SiadapRowProps) {
  const [expanded, setExpanded] = useState(false);

  const func = funcionarios.find((f) => f.id === registo.funcionarioId);
  const aval = avaliacaoConfig[registo.avaliacao as AvaliacaoKey] ?? fallbackConfig;
  // Calcula pontos usando a escala correta conforme o período
  const pontos = pontosParaAvaliacao(registo.avaliacao, registo.periodoAvaliacao ?? '');
  const escalaAntiga = isEscalaAntiga(registo.periodoAvaliacao ?? '');
  const diff = (registo.pontosCCA - registo.pontosChefe).toFixed(1);
  const diffNum = parseFloat(diff);

  const initials = func
    ? func.nome.split(' ').filter((_, i, arr) => i === 0 || i === arr.length - 1).map((n) => n[0]).join('')
    : '?';
  const colorIdx = func ? parseInt(func.id.replace(/\D/g, ''), 10) % avatarColors.length : 0;
  const colorClass = avatarColors[colorIdx];

  const hasDescricao = registo.descricaoSubida && registo.descricaoSubida.trim().length > 0;

  return (
    <>
      <tr
        className={`border-b border-slate-50 transition-colors group ${expanded ? 'bg-slate-50/80' : 'hover:bg-slate-50/60'}`}
      >
        {/* Funcionário + expand */}
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setExpanded((v) => !v)}
              disabled={!hasDescricao}
              title={hasDescricao ? (expanded ? 'Recolher descrição' : 'Ver descrição da subida') : 'Sem descrição'}
              className={`w-6 h-6 flex items-center justify-center rounded-md transition-all cursor-pointer flex-shrink-0 ${
                hasDescricao
                  ? expanded
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  : 'bg-slate-50 text-slate-300 cursor-default'
              }`}
            >
              <i className={`ri-${expanded ? 'subtract' : 'add'}-line text-xs`} />
            </button>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${colorClass}`}>
              {initials}
            </div>
            <div>
              <button
                onClick={() => func && onNameClick(func)}
                className="whitespace-nowrap text-sm font-medium text-slate-800 hover:text-emerald-700 hover:underline transition-colors cursor-pointer text-left"
                title="Ver histórico de avaliações"
              >
                {func?.nome ?? '—'}
              </button>
              <p className="text-xs text-slate-400">#{registo.funcionarioId}</p>
            </div>
          </div>
        </td>

        {/* Período */}
        <td className="px-5 py-3.5">
          <span className="text-sm text-slate-600 font-medium">{registo.periodoAvaliacao}</span>
        </td>

        {/* Avaliação + pontos equivalentes */}
        <td className="px-5 py-3.5">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${aval.bg} ${aval.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${aval.dot}`} />
                {registo.avaliacao}
              </span>
              <span
                className={`inline-flex items-center justify-center min-w-[2.5rem] px-1.5 py-0.5 rounded text-xs font-bold ${aval.bg} ${aval.text}`}
                title={`Pontos pela escala ${escalaAntiga ? '2000–2024' : '2025+'}`}
              >
                {pontos % 1 === 0 ? pontos.toFixed(0) : pontos.toFixed(1)} pts
              </span>
            </div>
            {/* Badge do período */}
            <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded w-fit ${
              escalaAntiga
                ? 'bg-slate-100 text-slate-500'
                : 'bg-teal-50 text-teal-600'
            }`}>
              <i className={`${escalaAntiga ? 'ri-time-line' : 'ri-calendar-check-line'} text-xs`} />
              {escalaAntiga ? '2000–2024' : '2025+'}
            </span>
          </div>
        </td>

        {/* Pontos Chefia */}
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-slate-500 rounded-full" style={{ width: `${(registo.pontosChefe / 5) * 100}%` }} />
            </div>
            <span className="text-sm font-medium text-slate-700">{registo.pontosChefe.toFixed(1)}</span>
          </div>
        </td>

        {/* Pontos CCA */}
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(registo.pontosCCA / 5) * 100}%` }} />
            </div>
            <span className="text-sm font-medium text-slate-700">{registo.pontosCCA.toFixed(1)}</span>
            <span className={`text-xs font-semibold ${diffNum > 0 ? 'text-emerald-600' : diffNum < 0 ? 'text-red-500' : 'text-slate-400'}`}>
              {diffNum > 0 ? '+' : ''}{diff}
            </span>
          </div>
        </td>

        {/* Nível */}
        <td className="px-5 py-3.5">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-sm font-bold text-slate-700">
            {registo.nivel}
          </span>
        </td>

        {/* Remuneração */}
        <td className="px-5 py-3.5">
          <span className="text-sm font-semibold text-slate-800">
            {registo.remuneracaoAtual.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
          </span>
        </td>

        {/* Ações */}
        <td className="px-5 py-3.5">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onEdit}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              title="Editar"
            >
              <i className="ri-edit-line text-sm" />
            </button>
            <button
              onClick={onDelete}
              className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
              title="Eliminar"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded row — descrição da subida */}
      {expanded && hasDescricao && (
        <tr className="border-b border-slate-100 bg-slate-50/80">
          <td colSpan={8} className="px-5 pb-4 pt-0">
            <div className="ml-[3.75rem] flex items-start gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 flex-shrink-0 mt-0.5">
                <i className="ri-file-text-line text-base" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Descrição da Subida</p>
                <p className="text-sm text-slate-700 leading-relaxed">{registo.descricaoSubida}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
